/**
 * Mine rails at both tiers (integration plan slice 2b; lifted from the
 * dungeon-floors prototype's track kit in `dungeon-tile-kit.js`, whose rail
 * prisms `dungeon-rail-union.js` then merges). Routes are shortest 4-way walks
 * between room centres that avoid the exit and springs, up to three, joined
 * into one tree (no cycles, at most three links a tile). The track per tile:
 * straight runs with ties on one floor-wide lattice (`railTieSpacingPercent`;
 * lattice ties next to a bend, turnout or dead end are dropped), true
 * quarter-circle bends of short rail segments with two radial ties, turnouts
 * that draw the through line and one seeded diverging curve with ties that
 * stop short of the branch, and dead ends with two ties and a stop block. A
 * dark ballast bed and grime streaks lie under the track at Medium. Rails
 * are instanced boxes in the iron batch, ties in the timber batch: the
 * prototype's polygon union of rail prisms (no overlap at joints, a two-tone
 * crown) and the turnout's frog piece are not ported (known gaps). Seeded by
 * the floor's seed; nothing here blocks a tile.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import type * as THREE from 'three';
import { type Part, part } from './batch.ts';
import { presentation } from './constants.ts';
import { isOpen, orthogonalSteps, tileIndex, tileRandom } from './grid.ts';
import type { Track } from './lighting-plan.ts';
import { type Palette, shaded } from './palette.ts';

export type RailParts = {
  /** Rails and stop-block irons (iron batch). */
  readonly iron: readonly Part[];
  /** Ties and stop blocks (timber batch). */
  readonly timber: readonly Part[];
  /** Ballast and grime (floor batch, Medium only). */
  readonly bed: readonly Part[];
  readonly tracks: readonly Track[];
};

type Link = { readonly x: number; readonly z: number };

const k = presentation.rails;

export const buildRails = (floor: Floor, seed: number, theme: Theme, palette: Palette, budget: number): RailParts => {
  const rules = theme.propRules;
  const spacing = rules.railTieSpacingPercent / 100;
  const blocked = new Set<number>(floor.springs.map((s) => tileIndex(floor, s.pos.x, s.pos.y)));
  floor.tiles.forEach((t, i) => {
    if (t === 'exit') blocked.add(i);
  });
  const free = (x: number, y: number): boolean => isOpen(floor, x, y) && !blocked.has(tileIndex(floor, x, y));
  const anchor = (r: Floor['rooms'][number]): number | null => {
    const cx = r.x + Math.floor(r.w / 2);
    const cy = r.y + Math.floor(r.h / 2);
    return free(cx, cy) ? tileIndex(floor, cx, cy) : null;
  };
  const walk = (from: number, to: number): number[] => {
    const parent = new Map<number, number>([[from, from]]);
    const queue = [from];
    for (let head = 0; head < queue.length && !parent.has(to); head += 1) {
      const i = queue[head] ?? from;
      const x = i % floor.width;
      const y = Math.floor(i / floor.width);
      for (const [dx, dy] of orthogonalSteps) {
        const n = tileIndex(floor, x + dx, y + dy);
        if (free(x + dx, y + dy) && !parent.has(n)) {
          parent.set(n, i);
          queue.push(n);
        }
      }
    }
    if (!parent.has(to)) return [];
    const path = [to];
    for (let i = to; i !== from; ) {
      i = parent.get(i) ?? from;
      path.push(i);
    }
    return path.reverse();
  };

  /** The track's parts for a graph of tile links (`trackGeometry`). */
  const geometry = (graph: ReadonlyMap<number, ReadonlySet<number>>): { iron: Part[]; timber: Part[] } => {
    const iron: Part[] = [];
    const timber: Part[] = [];
    const weathered = (tile: number, n: number): THREE.Color => shaded(palette.sleeper, k.weather[0] + tileRandom(seed, tile, n, 57) * k.weather[1]);
    const tie = (tile: number, n: number, x: number, z: number, sx: number, sz: number, ry: number): void => {
      timber.push(part(tile, x, k.tieHeight / 2, z, [sx, k.tieHeight, sz], weathered(tile, n), { ry }));
    };
    const segment = (tile: number, ax: number, az: number, bx: number, bz: number): void => {
      const dx = bx - ax;
      const dz = bz - az;
      iron.push(part(tile, (ax + bx) / 2, k.tieHeight - k.seat + k.railHeight / 2, (az + bz) / 2, [Math.hypot(dx, dz) + k.overlap, k.railHeight, k.railWidth], palette.rail, { ry: -Math.atan2(dz, dx) }));
    };
    const bend = (a: Link, b: Link, x: number, z: number): { cx: number; cz: number; start: number; sweep: number } => {
      const cx = x + (a.x + b.x) * 0.5;
      const cz = z + (a.z + b.z) * 0.5;
      const start = Math.atan2(-b.z, -b.x);
      let sweep = Math.atan2(-a.z, -a.x) - start;
      if (sweep > Math.PI) sweep -= Math.PI * 2;
      if (sweep < -Math.PI) sweep += Math.PI * 2;
      return { cx, cz, start, sweep };
    };
    const straight = (n: number): boolean => {
      const e = graph.get(n);
      if (e === undefined || e.size !== 2) return false;
      const [p, q] = [...e];
      return p !== undefined && q !== undefined && (Math.abs(p - q) === 2 || Math.abs(p - q) === 2 * floor.width);
    };
    const G = k.gauge;
    for (const [tile, edges] of graph) {
      const x = tile % floor.width;
      const y = Math.floor(tile / floor.width);
      const z = -y;
      const links: Link[] = [...edges].map((n) => ({ x: (n % floor.width) - x, z: y - Math.floor(n / floor.width) }));
      const [a, b] = links;
      if (a === undefined) continue;
      const horizontal = a.x !== 0;
      if (links.length === 1) {
        // A dead end: two ties under the rails and one stop block seated on the last.
        for (const side of [-1, 1]) segment(tile, x + a.x * k.deadStart + (horizontal ? 0 : side * G), z + a.z * k.deadStart + (horizontal ? side * G : 0), x + a.x * k.edge + (horizontal ? 0 : side * G), z + a.z * k.edge + (horizontal ? side * G : 0));
        k.deadTies.forEach((off, n) => {
          tie(tile, n, x + a.x * off, z + a.z * off, horizontal ? k.tieWidth : k.tieLength, horizontal ? k.tieLength : k.tieWidth, 0);
        });
        const off = k.deadTies[0];
        timber.push(part(tile, x + a.x * off, k.tieHeight + k.buffer.lift, z + a.z * off, horizontal ? [k.buffer.size[0], k.buffer.size[1], k.buffer.size[2]] : [k.buffer.size[2], k.buffer.size[1], k.buffer.size[0]], palette.timber));
        continue;
      }
      // A turnout draws its through line and one seeded diverging curve.
      const branch = links.length === 3 ? links.find((c) => !links.some((o) => o.x === -c.x && o.z === -c.z)) : undefined;
      const through = branch === undefined ? [] : links.filter((c) => c !== branch);
      const skip = branch === undefined ? undefined : through[tileRandom(seed, tile, 0, 61) < 0.5 ? 0 : 1];
      for (let i = 0; i < links.length; i += 1) {
        for (let j = i + 1; j < links.length; j += 1) {
          const p = links[i];
          const q = links[j];
          if (p === undefined || q === undefined) continue;
          if (skip !== undefined && (p === skip || q === skip) && (p === branch || q === branch)) continue;
          if (p.x === -q.x && p.z === -q.z) {
            for (const side of [-1, 1]) segment(tile, x + p.x * k.edge - p.z * side * G, z + p.z * k.edge + p.x * side * G, x + q.x * k.edge - p.z * side * G, z + q.z * k.edge + p.x * side * G);
          } else {
            // Quarter-circle rails meet the neighbouring gauge points exactly.
            const c = bend(p, q, x, z);
            for (const radius of [0.5 - G, 0.5 + G]) {
              for (let n = 0; n < k.bendSegments; n += 1) {
                const t0 = c.start + (c.sweep * n) / k.bendSegments;
                const t1 = c.start + (c.sweep * (n + 1)) / k.bendSegments;
                segment(tile, c.cx + Math.cos(t0) * radius, c.cz + Math.sin(t0) * radius, c.cx + Math.cos(t1) * radius, c.cz + Math.sin(t1) * radius);
              }
            }
          }
        }
      }
      if (links.length === 2 && b !== undefined && a.x !== -b.x && a.z !== -b.z) {
        // Two radial ties per bend, their inner ends apart and inside the tile.
        const c = bend(a, b, x, z);
        k.bendTies.forEach((t, n) => {
          const angle = c.start + c.sweep * t;
          tie(tile, n, c.cx + Math.cos(angle) * k.bendTieRadius, c.cz + Math.sin(angle) * k.bendTieRadius, k.bendTie[0], k.bendTie[1], -angle);
        });
      } else if (branch !== undefined) {
        // Through ties stop short on the branch side; one long tie crosses the branch's approach.
        const alongX = branch.x === 0;
        const t = k.turnout;
        const len = t.inner + t.outer;
        const shift = (t.inner - t.outer) / 2;
        t.offsets.forEach((off, n) => {
          tie(tile, n, x + (alongX ? off : -branch.x * shift), z + (alongX ? -branch.z * shift : off), alongX ? k.tieWidth : len, alongX ? len : k.tieWidth, 0);
        });
        tie(tile, t.offsets.length, x + branch.x * t.branch, z + branch.z * t.branch, branch.x !== 0 ? t.branchWidth : k.tieLength, branch.x !== 0 ? k.tieLength : t.branchWidth, 0);
      } else {
        // A straight run: ties on one floor-wide lattice along the track axis.
        const c = horizontal ? x : y;
        for (let n = Math.ceil((c - 0.5) / spacing - 1e-9); n * spacing < c + 0.5 - 1e-9; n += 1) {
          const off = n * spacing - c;
          const side = Math.sign(off);
          const next = horizontal ? tile + side : tile + side * floor.width;
          if (Math.abs(off) > k.latticeReach && !straight(next)) continue;
          const j = tileRandom(seed, tile, n, 58) - 0.5;
          const length = k.tieLength + (tileRandom(seed, tile, n, 59) - 0.5) * k.lengthJitter;
          const u = (horizontal ? off : -off) + j * k.skew;
          tie(tile, n, x + (horizontal ? u : 0), z + (horizontal ? 0 : u), horizontal ? k.tieWidth : length, horizontal ? length : k.tieWidth, j * k.skewTurn);
        }
      }
    }
    return { iron, timber };
  };

  const anchors = floor.rooms.map(anchor);
  const candidates: { path: number[]; a: number; b: number; rank: number }[] = [];
  anchors.forEach((from, a) => {
    anchors.forEach((to, b) => {
      if (b <= a || from === null || to === null) return;
      const path = walk(from, to);
      if (path.length > 1) candidates.push({ path, a, b, rank: path.length * (k.routeRank[0] + tileRandom(seed, a, b, 30) * k.routeRank[1]) });
    });
  });
  candidates.sort((p, q) => p.rank - q.rank || p.a - q.a || p.b - q.b);
  let graph = new Map<number, Set<number>>();
  let routes = 0;
  for (const c of candidates) {
    if (routes >= k.maxRoutes) break;
    if (tileRandom(seed, c.a, c.b, 31) > rules.railsDensityPercent / 100) continue;
    const next = new Map([...graph].map(([key, v]) => [key, new Set(v)]));
    for (let i = 1; i < c.path.length; i += 1) {
      const p = c.path[i - 1] ?? 0;
      const q = c.path[i] ?? 0;
      next.set(p, (next.get(p) ?? new Set()).add(q));
      next.set(q, (next.get(q) ?? new Set()).add(p));
    }
    // A tile joins at most three directions (a turnout), and the track stays a tree.
    if ([...next.values()].some((edges) => edges.size > 3)) continue;
    const links = [...next.values()].reduce((n, edges) => n + edges.size, 0) / 2;
    if (links !== next.size - countTrees(next)) continue;
    const parts = geometry(next);
    if (parts.iron.length + parts.timber.length > budget) continue;
    graph = next;
    routes += 1;
  }
  const { iron, timber } = geometry(graph);
  const bed: Part[] = [];
  const ballast = shaded(palette.floor, k.ballast.shade);
  const grime = shaded(palette.floor, k.grime.shade);
  for (const [tile, edges] of graph) {
    const x = tile % floor.width;
    const y = Math.floor(tile / floor.width);
    const links = [...edges].map((n) => [(n % floor.width) - x, Math.floor(n / floor.width) - y] as const);
    const [first] = links;
    const dead = links.length === 1 && first !== undefined;
    const b = k.ballast;
    if (dead) bed.push(part(tile, x + first[0] * b.deadOffset, b.y, -y - first[1] * b.deadOffset, first[0] !== 0 ? [b.dead, b.height, b.size] : [b.size, b.height, b.dead], ballast, { mediumOnly: true }));
    else bed.push(part(tile, x, b.y, -y, [b.size, b.height, b.size], ballast, { mediumOnly: true }));
    // Grime streaks along the track, flat on the ballast, tops stepped so none are coplanar.
    const [p, q] = links;
    const axis = links.length === 2 && p !== undefined && q !== undefined && p[0] === -q[0] && p[1] === -q[1] ? p : dead ? first : ([0, 0] as const);
    const g = k.grime;
    for (let n = 0; n < g.count; n += 1) {
      if (tileRandom(seed, tile, n, 140) > rules.railGrimePercent / 100) continue;
      const u = tileRandom(seed, tile, n, 141);
      const v = tileRandom(seed, tile, n, 142);
      const w = tileRandom(seed, tile, n, 143);
      const top = g.top + (((x + y) & 1) * g.count + n) * g.step;
      const along = dead ? g.deadAlong[0] + u * g.deadAlong[1] : (u - 0.5) * g.along;
      const across = (v - 0.5) * g.across;
      const [ax, ay] = axis;
      const yaw = (ax !== 0 || ay !== 0 ? (ax !== 0 ? 0 : Math.PI / 2) : w * Math.PI) + (w - 0.5) * g.turn;
      const gx = ax !== 0 || ay !== 0 ? x + ax * along - ay * across : x + (u - 0.5) * g.loose;
      const gz = ax !== 0 || ay !== 0 ? -y - ay * along - ax * across : -y + (v - 0.5) * g.loose;
      bed.push(part(tile, gx, top / 2, gz, [g.length[0] + w * g.length[1], top, g.width[0] + w * g.width[1]], grime, { ry: yaw, mediumOnly: true }));
    }
  }
  const tracks = [...graph].map(([tile, edges]): Track => ({ tile, neighbors: [...edges] }));
  return { iron, timber, bed, tracks };
};

/** Connected components of a link graph (a forest has links = nodes - components). */
const countTrees = (graph: ReadonlyMap<number, ReadonlySet<number>>): number => {
  const seen = new Set<number>();
  let trees = 0;
  for (const start of graph.keys()) {
    if (seen.has(start)) continue;
    trees += 1;
    const stack = [start];
    seen.add(start);
    while (stack.length > 0) {
      const n = stack.pop() ?? start;
      for (const m of graph.get(n) ?? []) {
        if (!seen.has(m)) {
          seen.add(m);
          stack.push(m);
        }
      }
    }
  }
  return trees;
};
